import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { LoroDoc } from "loro-crdt";
import { openDoc, type DocHandle, type DocKind, type SyncStatus } from "./docManager";

/** Open a doc for the component's lifetime (ref-counted). */
export function useDoc(kind: DocKind, id: string): DocHandle | null {
  const [handle, setHandle] = useState<DocHandle | null>(null);
  useEffect(() => {
    if (!id) return; // nothing to open (e.g. a block rendered without its workspace)
    let alive = true;
    let h: DocHandle | null = null;
    void openDoc(kind, id).then((opened) => {
      if (!alive) {
        opened.release();
        return;
      }
      h = opened;
      setHandle(opened);
    });
    return () => {
      alive = false;
      h?.release();
      setHandle(null);
    };
  }, [kind, id]);
  return handle;
}

/**
 * Read derived state from a Loro doc with useSyncExternalStore over `doc.subscribe` (spec §8.4).
 * `selector` must return a value that is stable across renders when nothing changed; a version
 * counter keyed on the doc's events triggers re-selection.
 */
export function useLoroSelector<T>(doc: LoroDoc | null, selector: (doc: LoroDoc) => T, isEqual: (a: T, b: T) => boolean = Object.is): T | null {
  const cache = useRef<{ version: number; value: T } | null>(null);
  const versionRef = useRef(0);
  const subscribe = (cb: () => void) => {
    if (!doc) return () => {};
    const unsub = doc.subscribe(() => {
      versionRef.current += 1;
      cb();
    });
    return () => unsub();
  };
  const getSnapshot = () => {
    if (!doc) return null;
    const v = versionRef.current;
    if (cache.current && cache.current.version === v) return cache.current.value;
    const next = selector(doc);
    if (cache.current && isEqual(cache.current.value, next)) {
      cache.current = { version: v, value: cache.current.value };
      return cache.current.value;
    }
    cache.current = { version: v, value: next };
    return next;
  };
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Whether the document's first join attempt has finished (see `DocHandle.joinSettled`). */
export function useJoinSettled(handle: DocHandle | null): boolean {
  return useSyncExternalStore(
    (onChange) => (handle ? handle.subscribeStatus(() => onChange()) : () => {}),
    () => !!handle?.joinSettled,
    () => false,
  );
}

export function useSyncStatus(handle: DocHandle | null): SyncStatus {
  const [status, setStatus] = useState<SyncStatus>(handle?.status ?? "offline");
  useEffect(() => {
    if (!handle) return;
    return handle.subscribeStatus(setStatus);
  }, [handle]);
  return status;
}
